<script setup lang="ts">
import { onMounted, reactive, ref, shallowRef, watch, type Component } from 'vue';

import { date, euro } from '@/helpers';
import { api } from '@/services/api';
import { alternative, form, setErrors } from '.';
import { back, calculateCurrentAgeAtDateOfDeparture, useFilters } from '..';

import Loading from '@/components/Loading.vue';
import PriceImformation from './components/PriceImformation.vue';
import Booking from './steps/Booking.vue';
import Personal from './steps/Personal.vue';
import Success from './steps/Success.vue';

import type { Book } from '@/types/book';

export interface Step {
  step: number;
  title: string;
  button?: string;
  component: Component;
}

const { start, searchParams, show, loading, success, featured, getFeatured } = useFilters();
const { scrollTo } = window;

const ages = reactive<string[]>([]);
const bookingLoading = ref<boolean>(false);
const key = ref<number>(0);
const currentStep = ref<number>(0);
const successSteps = 2;

const steps = shallowRef<Step[]>([
  {
    step: 0,
    title: 'Reisgegevens',
    button: 'Reisgezelschap',
    component: Booking,
  },
  {
    step: 1,
    title: 'Reisgezelschap',
    button: 'Reis boeken',
    component: Personal,
  },
  {
    step: 2,
    title: 'Success',
    component: Success,
  },
]);

async function book() {
  form.value.skipass_id = start.value?.pricing.skipass_id ?? '';

  bookingLoading.value = true;

  try {
    const { data } = await api.post<Book>('/book/make?brand=2', form.value);

    if (!data.success) {
      setErrors(data.errors);
      scrollTo({ top: 0, behavior: 'smooth' });

      return;
    }

    currentStep.value = successSteps;
  } catch (errors) {
    console.log(errors);
  } finally {
    bookingLoading.value = false;
  }
}

watch(
  () => ({
    passengers: form.value.passengers,
    dateOfBirth: form.value.date_of_birth,
  }),
  ({ passengers, dateOfBirth }) => {
    const passengerAges = passengers.map(({ date_of_birth }) => calculateCurrentAgeAtDateOfDeparture(new Date(date_of_birth)).toString());

    if (dateOfBirth) {
      const mainAge = calculateCurrentAgeAtDateOfDeparture(new Date(dateOfBirth)).toString();
      passengerAges.unshift(mainAge);
    }

    ages.splice(0, ages.length, ...passengerAges);
  },
  { deep: true }
);

watch(
  () => [...ages],
  (newAges) => {
    searchParams.value.delete('ages[]');
    newAges.forEach((age) => {
      searchParams.value.append('ages[]', age);
    });

    show(true, true);
  }
);

watch(
  [() => form.value.flight_id, () => form.value.accommodation_id, () => form.value.room_id],
  ([newVal]) => {
    if (form.value.flight_id && searchParams.value.get('flight_id') !== String(newVal)) {
      key.value++;
      searchParams.value.set('flight_id', String(newVal));
      show(true, true);
    }
  },
  { deep: true }
);

function nextStep() {
  currentStep.value++;
  scrollTo({ top: 0, behavior: 'smooth' });
}

onMounted(() => show(true));
</script>

<template>
  <Loading v-model:loading="loading" :fallback="success === false" @fallback="getFeatured">
    <template #fallback>
      <div class="loader__empty">
        <div class="--inner">
          <span class="icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="32" height="32">
              <path
                d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"
              />
            </svg>
          </span>
          <h2 class="block__title">Deze accommodatie is niet meer beschikbaar</h2>
          <p class="block__text">Deze accommodatie is helaas niet meer beschikbaar. Probeer een andere zoekopdracht of vraag vrijblijvend een offerte aan via de chat.</p>
          <a class="button back" href="/wintersportreizen">Ga terug</a>
        </div>
      </div>
      <template v-if="featured">
        <div class="container">
          <div class="row m-auto">
            <div class="block__content">
              <div class="block__header mb-3">
                <h3 class="block__title mb-0">Bekijk ook één van onderstaande reisopties</h3>
              </div>
            </div>
          </div>
          <div class="row">
            <template v-for="(item, index) in featured" :key="index">
              <div class="col-md-6">
                <a class="block block--package" :href="item.pricing.accommodationurl" :title="item.room.accommodation.name" tarindex="_top">
                  <p class="block__img__meta">{{ date.format(new Date(item.date)) }}, {{ item.persons }} personen</p>
                  <img defer v-if="item.room.accommodation.photos[0]" :src="item.room.accommodation.photos[0].url" class="block__img" />
                  <div class="block__content">
                    <div class="block__header">
                      <h3 class="block__title">{{ item.room.accommodation.name }}</h3>
                      <Ratings :rating="item.room.rating" />
                    </div>
                    <ul>
                      <template v-for="({ title }, index) in item.pricing.included" :key="index">
                        <li>{{ title }}</li>
                      </template>
                      <li>Gratis eigen ski’s mee op de vlucht</li>
                    </ul>
                  </div>
                  <div class="block__footer">
                    <div class="block__price">
                      <span class="block__price">
                        {{ euro.format(item.pricing.price.perperson.amount) }}
                      </span>
                      <span class="block__price"> p.p. </span>
                    </div>
                    <a class="button" title="" :href="item.pricing.accommodationurl">Bekijk</a>
                  </div>
                </a>
              </div>
            </template>
          </div>
        </div>
      </template>
    </template>
    <section class="section section--booking">
      <div v-if="currentStep !== successSteps" class="section__header" :class="{ header__100: currentStep === 1 }">
        <div class="container">
          <div class="section__wrap">
            <h1 class="section__title">Boek jouw reis</h1>
            <ol>
              <template v-for="(step, index) in steps" :key="index">
                <template v-if="step.step !== successSteps">
                  <li :class="{ active: step.step === currentStep || step.step === 0 }">{{ step.title }}</li>
                </template>
              </template>
            </ol>
          </div>
        </div>
      </div>
      <div class="container">
        <div class="row">
          <div class="col-lg-7">
            <component :key="steps[currentStep].step" :is="steps[currentStep].component" :booking="start" v-model:form="form" />
          </div>
          <div class="col-lg-4 offset-lg-1">
            <PriceImformation :step="currentStep" :booking="start" :key="key" />
          </div>
        </div>
      </div>
      <div v-if="currentStep !== successSteps" class="section__footer">
        <div class="container">
          <div class="section__buttons">
            <button v-if="currentStep !== 0" class="button back" @click="currentStep !== 0 && --currentStep">Vorige</button>
            <a v-if="currentStep === 0" class="link back accommodation" @click="back()"> TERUG NAAR ACCOMMODATIE </a>
            <button v-if="!alternative" class="button" @click="currentStep !== steps.length - successSteps ? nextStep() : book()">
              {{ steps[currentStep].button }}
            </button>
          </div>
        </div>
      </div>
    </section>
  </Loading>
</template>
